import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle} from "reactstrap"
import {message} from "antd"
import {exportLayerConfig, fileErrorMessage} from '../../../services/api/apiSku'
import {downloadBlob, exportStamp} from '../../ProductionPlanning/helpers/download'
import ImportLayerConfigModal from "./ImportLayerConfigModal"

const ITEM_STYLE = {marginBottom: 0}

/**
 * Nhập / xuất cấu trúc layer NVL của SKU (FEAT-2256 · T8).
 *
 * Gộp hai hành động vào MỘT nút đổ xuống thay vì hai nút rời:
 *   - Header màn này đã có 4 phần tử, thêm hai nút nữa là chật.
 *   - Và quan trọng hơn: header đã có sẵn một nút nhãn "Import" (actions/Import.js) dùng để nạp
 *     GIÁ VỐN. Hai nút "Import" cạnh nhau, cùng nhận .csv nhưng nạp hai loại dữ liệu khác hẳn —
 *     mà cả hai đều là thao tác ghi đè. Gom vào nhóm "Cấu trúc layer" thì mỗi mục nằm dưới đúng
 *     tiêu đề nói rõ nó thao tác trên cái gì.
 *
 * Xuất file bám BỘ LỌC ĐANG ÁP DỤNG trên màn — `filters` là đúng object mà
 * `SkuPage._fetchListSku` gửi lên `POST /sku/search`.
 *
 * ⚠️ Nhưng KHÔNG bám `page`/`limit`: file chứa MỌI dòng khớp bộ lọc, không phải 20 dòng đang
 *    hiển thị. Cố ý — xuất một trang thì file mẫu thiếu SKU. Hệ quả cần biết: vào màn rồi bấm
 *    Export ngay khi chưa lọc gì sẽ xuất TOÀN BỘ bảng SKU, và đó cũng là file người dùng sẽ điền
 *    rồi nạp lại bằng cơ chế ghi đè.
 *
 * File xuất ra cũng đóng vai trò FILE MẪU để nạp lại: lọc ra tập SKU cần khai, Export, điền vào
 * các ô layer còn trống rồi Import. Cố ý không dựng file mẫu tĩnh riêng — mã SKU gõ tay là nguồn
 * lỗi "SKU không tồn tại" phổ biến nhất, và số cột layer của file mẫu tĩnh sẽ lệch với file BE sinh.
 */
class LayerConfigActions extends Component {
    state = {
        dropdownOpen: false,
        exporting: false,
        isImportOpen: false,
    }

    componentWillUnmount() {
        this._unmounted = true
    }

    _toggleDropdown = () => {
        this.setState({
            dropdownOpen: !this.state.dropdownOpen,
        })
    }

    _toggleImportModal = () => {
        this.setState({
            isImportOpen: !this.state.isImportOpen,
        })
    }

    _handleExport = async () => {
        if (this.state.exporting) return

        this.setState({exporting: true})

        try {
            const response = await exportLayerConfig(this.props.filters)

            /*
             * TỰ kiểm status — bắt buộc, và phải đứng TRƯỚC mọi xử lý thân response.
             *
             * `exportLayerConfig` đi qua `requestFile`, vốn đặt `validateStatus: () => true` để
             * interceptor lỗi của `makeApiRequest` không phá huỷ Blob. Đổi lại axios không còn
             * reject, nên không kiểm ở đây thì một cú 403 sẽ được tải xuống thành file .csv chứa
             * thân JSON lỗi và người dùng tưởng là đã xuất thành công.
             */
            if (response.status >= 400) {
                message.error(await fileErrorMessage(response) || 'Không xuất được file. Vui lòng thử lại')
                return
            }

            /*
             * Status < 400 CHƯA đủ để coi là có file. Controller BE (`skuController.exportLayerConfig`)
             * khi action lỗi gọi `sendError`, mà `sendError` chỉ `res.send({success: false, message})`
             * KHÔNG đặt status ⇒ HTTP 200 + `application/json`. Thiếu bước này thì thân JSON lỗi bị
             * tải xuống thành `sku-layer-config-*.csv` kèm thông báo thành công — đúng file người
             * dùng sẽ điền rồi nạp lại bằng cơ chế ghi đè.
             *
             * Nhánh thành công đi qua `res.attachment(*.csv)` ⇒ `text/csv`. Khớp cùng điều kiện với
             * nhánh file lỗi của ImportLayerConfigModal: chỉ nhận `csv` / `octet-stream` (proxy tước
             * kiểu cụ thể); mọi kiểu khác — JSON lỗi, trang HTML của gateway — đều không phải file.
             */
            const contentType = `${response.headers['content-type'] || ''}`.toLowerCase()

            if (!contentType.includes('csv') && !contentType.includes('octet-stream')) {
                message.error(await fileErrorMessage(response) || 'Không xuất được file. Vui lòng thử lại')
                return
            }

            // Phải tự dựng link tải. Chỉ `await` rồi báo thành công thì KHÔNG có file nào rơi
            // xuống máy trong khi màn hình vẫn báo thành công (xem helpers/download.js).
            downloadBlob(response.data, `sku-layer-config-${exportStamp()}.csv`)

            message.success('Đã tải file cấu trúc layer')
        } catch (e) {
            // validateStatus nuốt mọi status, nên tới đây chỉ còn lỗi mạng / request bị huỷ.
            message.error('Không xuất được file. Vui lòng thử lại')
        } finally {
            // Rời màn giữa lúc export đang chạy thì component đã unmount — setState lúc đó là
            // no-op kèm warning. `message` của antd là global nên các nhánh trên vẫn báo được.
            if (!this._unmounted) this.setState({exporting: false})
        }
    }

    render() {
        const {dropdownOpen, exporting, isImportOpen} = this.state
        const {onImported} = this.props

        return (
            <div className="LayerConfigActions d-inline-block ml-3">
                <ButtonDropdown isOpen={dropdownOpen} toggle={this._toggleDropdown}>
                    <DropdownToggle caret color="secondary" disabled={exporting}>
                        {exporting && <i className='fa fa-spin fa-spinner mr-2'/>}
                        Cấu trúc layer
                    </DropdownToggle>
                    <DropdownMenu right>
                        {/*
                          * `marginBottom: 0` là cố ý: DropdownItem render ra <button>, mà
                          * `.header-elements button` trong header-elements-inline đặt
                          * `margin-bottom: 5px` (_header-elements.scss:121-123). Không ghi đè thì
                          * menu có khe hở giữa các dòng, khác mọi dropdown còn lại của app.
                          */}
                        <DropdownItem style={ITEM_STYLE} onClick={this._toggleImportModal}>
                            Import cấu trúc
                        </DropdownItem>
                        <DropdownItem style={ITEM_STYLE} disabled={exporting} onClick={this._handleExport}>
                            Export cấu trúc
                        </DropdownItem>
                    </DropdownMenu>
                </ButtonDropdown>

                {isImportOpen && (
                    <ImportLayerConfigModal
                        isOpen={isImportOpen}
                        onToggleModal={this._toggleImportModal}
                        onImported={onImported}
                    />
                )}
            </div>
        )
    }
}

LayerConfigActions.propTypes = {
    filters: PropTypes.object,
    onImported: PropTypes.func,
}

LayerConfigActions.defaultProps = {
    filters: {},
    onImported: () => {},
}

export default LayerConfigActions
